import { Role } from '@app/domain/iam/user.interface'
import type { AppAction } from '@app/state'

export const enum AuthMethod {
  OAuth = 'oauth',
  Credentials = 'credentials',
}

export const enum AuthActionStatus {
  Pending = 'pending',
  Succeeded = 'succeeded',
  Failed = 'failed',
}

export const enum OAuthStage {
  Code = 'code',
  Token = 'token',
}

export const enum CredentialsStage {
  Token = 'token',
}

export type PendingState =
  | { method: AuthMethod.OAuth; stage: OAuthStage }
  | { method: AuthMethod.Credentials; state: CredentialsStage }

export interface State {
  identifier?: string
  role?: Role
  token?: string
  expiryDate?: string
  pendingState?: PendingState
  authPasswordStatus?: AuthActionStatus
  errorMessage?: string
  forgotPasswordStatus?: AuthActionStatus
  passwordResetStatus?: AuthActionStatus
}

export enum ActionType {
  AssumeRole = 'AUTH/ASSUME_ROLE',
  Credentials = 'AUTH/CREDENTIALS',
  CredentialsBegin = 'AUTH/CREDENTIALS/BEGIN',
  CredentialsSuccess = 'AUTH/CREDENTIALS/SUCCESS',
  CredentialsFailure = 'AUTH/CREDENTIALS/FAILURE',
  OAuth = 'AUTH/OAUTH',
  OAuthBegin = 'AUTH/OAUTH/BEGIN',
  OAuthCodeReceived = 'AUTH/OAUTH/CODE_RECEIVED',
  OAuthSuccess = 'AUTH/OAUTH/SUCCESS',
  OAuthFailure = 'AUTH/OAUTH/FAILURE',
  UserProfileLoaded = 'AUTH/USER_PROFILE_LOADED',
  AuthenticationCallback = 'AUTH/AUTHENTICATION_CALLBACK',
  RestoreAuthToken = 'AUTH/RESTORE_AUTH_TOKEN',
  ForgotPassword = 'AUTH/FORGOT_PASSWORD',
  ForgotPasswordBegin = 'AUTH/FORGOT_PASSWORD/BEGIN',
  ForgotPasswordSuccess = 'AUTH/FORGOT_PASSWORD/SUCCESS',
  ForgotPasswordFailure = 'AUTH/FORGOT_PASSWORD/FAILURE',
  ResetPassword = 'AUTH/RESET_PASSWORD',
  ResetPasswordBegin = 'AUTH/RESET_PASSWORD/BEGIN',
  ResetPasswordSuccess = 'AUTH/RESET_PASSWORD/SUCCESS',
  ResetPasswordFailure = 'AUTH/RESET_PASSWORD/FAILURE',
  Logout = 'AUTH/LOGOUT',
}

export type Action =
  | { type: ActionType.AssumeRole; role: Role }
  | { type: ActionType.Credentials; identifier: string; password: string }
  | { type: ActionType.CredentialsBegin }
  | {
      type: ActionType.CredentialsSuccess
      token: string
      expiryDate: string
      role: Role
    }
  | { type: ActionType.CredentialsFailure; error: string }
  | { type: ActionType.OAuth }
  | { type: ActionType.OAuthBegin }
  | { type: ActionType.OAuthCodeReceived }
  | { type: ActionType.OAuthSuccess; token: string; expiryDate: string }
  | { type: ActionType.OAuthFailure; message: string }
  | { type: ActionType.UserProfileLoaded }
  | { type: ActionType.AuthenticationCallback }
  | {
      type: ActionType.RestoreAuthToken
      identifier: string
      token: string
      expiryDate: string
      role: Role
    }
  | { type: ActionType.ForgotPassword; email: string }
  | { type: ActionType.ForgotPasswordBegin }
  | { type: ActionType.ForgotPasswordSuccess }
  | { type: ActionType.ForgotPasswordFailure; error: string }
  | { type: ActionType.ResetPassword; requestId: string; password: string }
  | { type: ActionType.ResetPasswordBegin }
  | { type: ActionType.ResetPasswordSuccess }
  | { type: ActionType.ResetPasswordFailure; error: string }
  | { type: ActionType.Logout }

export const actions = {
  credentials(identifier: string, password: string): Action {
    return { type: ActionType.Credentials, identifier, password }
  },
  credentialsBegin(): Action {
    return { type: ActionType.CredentialsBegin }
  },
  credentialsSuccess(token: string, expiryDate: string, role: Role): Action {
    return { type: ActionType.CredentialsSuccess, token, expiryDate, role }
  },
  credentialsFailure(error: string): Action {
    return { type: ActionType.CredentialsFailure, error }
  },
  oauth(): Action {
    return { type: ActionType.OAuth }
  },
  oauthBegin(): Action {
    return { type: ActionType.OAuthBegin }
  },
  oauthCodeReceived(): Action {
    return { type: ActionType.OAuthCodeReceived }
  },
  oauthSuccess(token: string, expiryDate: string): Action {
    return { type: ActionType.OAuthSuccess, token, expiryDate }
  },
  oauthFailure(message: string): Action {
    return { type: ActionType.OAuthFailure, message }
  },
  userProfileLoaded(): Action {
    return { type: ActionType.UserProfileLoaded }
  },
  authenticationCallback(): Action {
    return { type: ActionType.AuthenticationCallback }
  },
  assumeRole(role: Role): Action {
    return { type: ActionType.AssumeRole, role }
  },
  restoreAuthToken(
    identifier: string,
    token: string,
    expiryDate: string,
    role: Role,
  ): Action {
    return {
      type: ActionType.RestoreAuthToken,
      identifier,
      token,
      expiryDate,
      role,
    }
  },
  forgotPassword(email: string): Action {
    return { type: ActionType.ForgotPassword, email }
  },
  forgotPasswordBegin(): Action {
    return { type: ActionType.ForgotPasswordBegin }
  },
  forgotPasswordSuccess(): Action {
    return { type: ActionType.ForgotPasswordSuccess }
  },
  forgotPasswordFailure(error: string): Action {
    return { type: ActionType.ForgotPasswordFailure, error }
  },
  resetPassword(requestId: string, password: string): Action {
    return { type: ActionType.ResetPassword, requestId, password }
  },
  resetPasswordBegin(): Action {
    return { type: ActionType.ResetPasswordBegin }
  },
  resetPasswordSuccess(): Action {
    return { type: ActionType.ResetPasswordSuccess }
  },
  resetPasswordFailure(error: string): Action {
    return { type: ActionType.ResetPasswordFailure, error }
  },
  logout(): Action {
    return { type: ActionType.Logout }
  },
}

export const initialState: State = {
  identifier: undefined,
  role: undefined,
  token: undefined,
  expiryDate: undefined,
  errorMessage: undefined,
  pendingState: undefined,
  authPasswordStatus: undefined,
  forgotPasswordStatus: undefined,
  passwordResetStatus: undefined,
}

export function reducer(state: State = initialState, action: AppAction): State {
  switch (action.type) {
    case ActionType.AssumeRole:
      return {
        ...state,
        role: action.role,
      }
    case ActionType.OAuthBegin:
      return {
        ...state,
        pendingState: {
          method: AuthMethod.OAuth,
          stage: OAuthStage.Code,
        },
        errorMessage: undefined,
      }
    case ActionType.OAuthCodeReceived:
      return {
        ...state,
        pendingState: {
          method: AuthMethod.OAuth,
          stage: OAuthStage.Token,
        },
        errorMessage: undefined,
      }
    case ActionType.OAuthSuccess:
      return {
        ...state,
        role: Role.Admin,
        errorMessage: undefined,
        pendingState: undefined,
        token: action.token,
        expiryDate: action.expiryDate,
      }
    case ActionType.OAuthFailure:
      return {
        ...state,
        role: undefined,
        pendingState: undefined,
        errorMessage: action.message,
      }
    case ActionType.Credentials:
      return {
        ...state,
        identifier: action.identifier,
      }
    case ActionType.CredentialsBegin:
      return {
        ...state,
        authPasswordStatus: AuthActionStatus.Pending,
      }
    case ActionType.CredentialsSuccess:
      return {
        ...state,
        role: Role.Admin,
        token: action.token,
        expiryDate: action.expiryDate,
        authPasswordStatus: AuthActionStatus.Succeeded,
      }
    case ActionType.CredentialsFailure:
      return {
        ...state,
        role: undefined,
        identifier: undefined,
        authPasswordStatus: AuthActionStatus.Failed,
        errorMessage: action.error,
      }
    case ActionType.RestoreAuthToken:
      return {
        ...state,
        identifier: action.identifier,
        token: action.token,
        expiryDate: action.expiryDate,
        role: action.role,
      }
    case ActionType.ForgotPassword: {
      return {
        ...state,
      }
    }
    case ActionType.ForgotPasswordBegin: {
      return {
        ...state,
        forgotPasswordStatus: AuthActionStatus.Pending,
      }
    }
    case ActionType.ForgotPasswordSuccess: {
      return {
        ...state,
        forgotPasswordStatus: AuthActionStatus.Succeeded,
      }
    }
    case ActionType.ForgotPasswordFailure: {
      return {
        ...state,
        forgotPasswordStatus: AuthActionStatus.Failed,
        errorMessage: action.error,
      }
    }
    case ActionType.ResetPassword: {
      return {
        ...state,
      }
    }
    case ActionType.ResetPasswordBegin: {
      return {
        ...state,
        passwordResetStatus: AuthActionStatus.Pending,
      }
    }
    case ActionType.ResetPasswordSuccess: {
      return {
        ...state,
        passwordResetStatus: AuthActionStatus.Succeeded,
      }
    }
    case ActionType.ResetPasswordFailure: {
      return {
        ...state,
        passwordResetStatus: AuthActionStatus.Failed,
        errorMessage: action.error,
      }
    }
    case ActionType.Logout: {
      return initialState
    }
    default:
      return state
  }
}

// Queries
//
export const queries = {
  hasErrors(state: State): boolean {
    return state.errorMessage !== undefined
  },
  hasRole(state: State, role: Role): boolean {
    return state.role === role
  },
  isAuthenticated(state: State): boolean {
    const expired = state.expiryDate
      ? new Date(state.expiryDate) < new Date()
      : true
    return state.token !== null && !expired
  },
  isPending(state: State): boolean {
    return state.authPasswordStatus === AuthActionStatus.Pending
  },
}
